import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from '../ui/CTAButton';
import { ZapIcon, DownloadIcon, UsersIcon } from 'lucide-react';
import { useCurrencyByIP } from '../../hooks/useCurrencyByIP';
function EbookMockup() {
  return <motion.div initial={{
    opacity: 0,
    x: 30,
    rotateY: -8
  }} animate={{
    opacity: 1,
    x: 0,
    rotateY: 0
  }} transition={{
    duration: 0.7,
    delay: 0.3,
    ease: [0.25, 0.1, 0.25, 1]
  }} className="relative flex items-center justify-center" style={{
    perspective: '1000px'
  }} data-id="element-139">
      {/* Glow behind the book */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(255,107,0,0.15) 0%, rgba(57,255,20,0.05) 40%, transparent 70%)',
      filter: 'blur(40px)'
    }} data-id="element-140" />

      {/* The ebook card */}
      <div className="relative w-[220px] h-[310px] md:w-[280px] md:h-[400px]" style={{
      transform: 'rotateY(-6deg) rotateX(2deg)',
      transformStyle: 'preserve-3d'
    }} data-id="element-141">
        {/* Spine shadow */}
        <div className="absolute -left-2 top-2 bottom-2 w-4 rounded-l-sm" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.1))',
        transform: 'translateZ(-2px)'
      }} data-id="element-142" />

        {/* Main cover */}
        <div className="relative w-full h-full rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60" data-id="element-143">
          {/* Gradient background */}
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(145deg, #18181B 0%, #09090B 50%, #1a0f00 100%)'
        }} data-id="element-144" />

          {/* Accent border glow */}
          <div className="absolute inset-0 rounded-xl" style={{
          background: 'linear-gradient(135deg, rgba(255,107,0,0.3) 0%, rgba(57,255,20,0.15) 50%, rgba(251,191,36,0.2) 100%)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
          padding: '1.5px'
        }} data-id="element-145" />

          {/* Content */}
          <div className="relative h-full flex flex-col items-center justify-between p-6 md:p-8" data-id="element-146">
            {/* Top badge */}
            <div className="flex items-center gap-1.5 bg-accent/15 border border-accent/30 rounded-full px-3 py-1" data-id="element-147">
              <ZapIcon className="w-3 h-3 text-accent" data-id="element-148" />
              <span className="text-[10px] md:text-xs font-bold text-accent tracking-wider uppercase" data-id="element-149">
                Meta Reels
              </span>
            </div>

            {/* Center content */}
            <div className="text-center space-y-3" data-id="element-150">
              {/* Icon */}
              <div className="mx-auto w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center" data-id="element-151">
                <ZapIcon className="w-6 h-6 md:w-7 md:h-7 text-accent" data-id="element-152" />
              </div>

              <div data-id="element-153">
                <h3 className="text-lg md:text-xl font-extrabold text-white tracking-tight leading-tight" data-id="element-154">
                  HOOK
                </h3>
                <h3 className="text-lg md:text-xl font-extrabold text-accent tracking-tight leading-tight" data-id="element-155">
                  ARSENAL
                </h3>
              </div>

              <div className="h-px w-12 mx-auto bg-gradient-to-r from-transparent via-accent/50 to-transparent" data-id="element-156" />

              <p className="text-xs md:text-sm font-bold text-neutral-400 tracking-widest uppercase" data-id="element-157">
                200 Ganchos
              </p>
              <p className="text-[10px] md:text-xs text-neutral-500 tracking-wider" data-id="element-158">
                Español MX
              </p>
            </div>

            {/* Bottom */}
            <div className="flex items-center gap-1.5" data-id="element-159">
              <div className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" data-id="element-160" />
              <span className="text-[9px] md:text-[10px] font-semibold text-neon/80 uppercase tracking-widest" data-id="element-161">
                PDF Digital
              </span>
            </div>
          </div>
        </div>

        {/* Page edges (right side) */}
        <div className="absolute top-3 -right-1 bottom-3 w-1.5 rounded-r-sm" style={{
        background: 'repeating-linear-gradient(to bottom, #27272A 0px, #27272A 2px, #1a1a1d 2px, #1a1a1d 4px)'
      }} data-id="element-162" />
      </div>
    </motion.div>;
}
export function HeroSection() {
  const {
    price,
    originalPrice
  } = useCurrencyByIP();
  return <section className="relative w-full pt-12 pb-12 md:pt-20 md:pb-24 px-4 md:px-5 overflow-hidden" data-id="element-163">
      {/* Radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] md:w-[900px] md:h-[600px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, rgba(255,107,0,0.07) 0%, rgba(57,255,20,0.02) 40%, transparent 70%)'
    }} data-id="element-164" />

      <div className="relative max-w-5xl mx-auto" data-id="element-165">
        {/* Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center" data-id="element-166">
          {/* Left column — Text (7 cols on desktop) */}
          <div className="md:col-span-7" data-id="element-167">
            {/* Eyebrow */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            delay: 0.05
          }} className="flex items-center gap-2 mb-5 md:mb-6" data-id="element-168">
              <ZapIcon className="w-4 h-4 text-accent" data-id="element-169" />
              <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent" data-id="element-170">
                Hook Arsenal — Meta Reels
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight mb-5" data-id="element-171">
              Tus Reels no fallan por el contenido.{' '}
              <span className="text-accent" data-id="element-172">Fallan en el primer segundo.</span>
            </motion.h1>

            {/* Mobile mockup — between headline and subheadline */}
            <div className="md:hidden flex justify-center my-8" data-id="element-173">
              <EbookMockup data-id="element-174" />
            </div>

            {/* Subheadline */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.18
          }} className="mb-8 max-w-xl" data-id="element-175">
              <p className="text-base md:text-xl text-neutral-400 leading-relaxed mb-3" data-id="element-176">
                Este PDF te da{' '}
                <span className="text-white font-semibold" data-id="element-177">
                  200 ganchos probados en Español MX
                </span>{' '}
                que hacen que la gente se quede, te siga y te compre.
              </p>
              <p className="text-base md:text-lg text-neutral-500" data-id="element-178">
                Sin improvisar. Sin pensar. Sin adivinar.
              </p>
            </motion.div>

            {/* CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.25
          }} className="space-y-4" data-id="element-179">
              {/* Price display */}
              <div className="flex items-center gap-3" data-id="element-180">
                <span className="text-lg text-neutral-500 line-through" data-id="element-181">
                  {originalPrice}
                </span>
                <span className="text-2xl md:text-3xl font-extrabold text-accent" data-id="element-182">
                  {price}
                </span>
              </div>

              <CTAButton href="#pricing" className="text-xl md:text-2xl py-5 md:py-6 px-10 md:px-14" data-id="element-183">
                Descargar Hook Arsenal — {price}
              </CTAButton>

              {/* Trust bullets */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-neutral-500 text-xs md:text-sm" data-id="element-184">
                <span data-id="element-185">✓ Descarga inmediata</span>
                <span data-id="element-186">✓ Acceso de por vida</span>
                <span data-id="element-187">✓ Funciona en Reels, TikTok e Instagram</span>
              </div>
            </motion.div>

            {/* Social proof */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.4
          }} className="mt-8 md:mt-10 flex items-center gap-3" data-id="element-188">
              <div className="flex items-center gap-2 bg-neon/10 border border-neon/20 rounded-full px-3.5 py-1.5" data-id="element-189">
                <UsersIcon className="w-3.5 h-3.5 text-neon" data-id="element-190" />
                <span className="text-xs md:text-sm font-bold text-neon" data-id="element-191">
                  +1,500
                </span>
              </div>
              <span className="text-sm text-neutral-400" data-id="element-192">
                creadores ya lo están usando
              </span>
            </motion.div>
          </div>

          {/* Right column — Ebook Mockup */}
          <div className="hidden md:flex md:col-span-5 justify-center" data-id="element-193">
            <EbookMockup data-id="element-194" />
          </div>
        </div>
      </div>
    </section>;
}